import { SharedProgrammeLayout } from '@/components/layout/SharedProgrammePage';
import { BASE_REVALIDATE } from '@/lib/constants';
import {
  allProgrammesQuery,
  navigationQuery,
  programmeQuery,
  siteSettingsQuery,
} from '@/lib/queries';
import { getClient, readToken } from '@/lib/sanityUtils';

import type {
  SanityNavigationType,
  SanityProgrammeType,
  SanitySiteSettingsType,
} from '@/types/sanity';
import type { Locale } from '@/types/shared';
import type { GetStaticPaths, GetStaticProps } from 'next';

const region: Locale = 'en-US';

export const getStaticPaths = (async () => {
  const allPages = await getClient().fetch(allProgrammesQuery({ region }));

  const paths = allPages.map((post) => ({
    params: { slug: post.slug.current },
    locale: post.region,
  }));

  return { paths, fallback: 'blocking' };
}) satisfies GetStaticPaths;

export const getStaticProps = (async ({
  params: { slug },
  draftMode = false,
}) => {
  const client = getClient(draftMode ? { token: readToken } : undefined);

  const [pageData, siteSettings, navigation] = await Promise.all([
    client.fetch<SanityProgrammeType>(programmeQuery({ slug, region })),

    client.fetch<SanitySiteSettingsType>(siteSettingsQuery({ locale: region })),

    client.fetch<SanityNavigationType>(navigationQuery({ locale: region })),
  ]);

  if (!pageData) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      programme: pageData,
      siteSettings,
      locale: region,
      draftMode,
      navigation,
      token: draftMode ? readToken : '',
    },
    revalidate: Number(process.env.REVALIDATE || BASE_REVALIDATE),
  };
}) satisfies GetStaticProps;

export default SharedProgrammeLayout;
